import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { navigateTo } from '../client/navigation.js';
import { loadBrowserProfile, type BrowserProfile } from '../client/profile.js';
import { usePresence, type PresenceProfileSource, type PresenceState } from '../client/presence.js';
import type { DeckApi } from '../decks/api.js';
import { createBrowserDeckApi } from '../decks/http.js';
import { DeckGlyphStrip } from '../decks/DeckGlyphStrip.js';
import { LobbyDeckModal, type SelectedDeckSummary } from '../decks/LobbyDeckModal.js';
import type { FastOnboardingProgress, OnboardingApi } from '../onboarding/api.js';
import { LobbyOnboardingTour } from '../onboarding/tours.js';
import { Button, PanelHeader, Screen } from './kit/index.js';
import { Logo } from './Logo.js';

const browserDeckApi = createBrowserDeckApi();

export interface LobbyProps {
  profile?: BrowserProfile;
  /** Account profile is server-authoritative and must never be copied to localStorage. */
  profileSource?: PresenceProfileSource;
  deckApi?: DeckApi;
  csrfToken?: string;
  /** Deck remembered in the account (server-authoritative), used to prefill the plaque. */
  initialSelectedDeck?: SelectedDeckSummary;
  onFindOpponent?: (deck: SelectedDeckSummary) => void;
  onPlayBot?: (deck: SelectedDeckSummary) => void;
  onChallenges?: () => void;
  /**
   * Active quick-match search. When present the lobby shows an inline timer in
   * place of the primary actions (and a cancel button) instead of routing to a
   * separate matchmaking screen.
   */
  search?: {
    startedAt: number;
    presence: PresenceState;
    connecting: boolean;
    reconnecting: boolean;
    connectionError?: string;
    onCancel: () => void;
  };
  onboardingTour?: {
    progress: FastOnboardingProgress;
    csrfToken: string;
    api: OnboardingApi;
    onCompleted: (progress: FastOnboardingProgress) => void;
  };
}

export function Lobby({
  profile: initialProfile,
  profileSource = 'local',
  deckApi = browserDeckApi,
  csrfToken = '',
  initialSelectedDeck,
  onFindOpponent = () => navigateTo('/?quick=1'),
  onPlayBot = () => navigateTo('/?solo=1'),
  onChallenges = () => navigateTo('/challenges'),
  search,
  onboardingTour,
}: LobbyProps): ReactElement {
  const [profile] = useState(() => initialProfile
    ?? loadBrowserProfile(window.localStorage, window.sessionStorage));
  const searching = search !== undefined;
  // While searching, presence comes from the caller's live heartbeat (which owns
  // the `searching: true` state for this seat). The lobby's own idle heartbeat is
  // disabled then so the two don't race on the same presence entry and flip the
  // global "searching" flag; otherwise the lobby polls lazily on its own.
  usePresence(searching ? undefined : profile, false, profileSource);
  const [selectedDeck, setSelectedDeck] = useState<SelectedDeckSummary | undefined>(initialSelectedDeck);
  const [deckModalOpen, setDeckModalOpen] = useState(false);

  // Live search timer, ticking once per second only while a search is active.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!searching) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(timer);
  }, [searching]);

  // Resolve the account's remembered deck for the plaque. Non-blocking: the
  // plaque simply shows "выберите колоду" until this settles.
  useEffect(() => {
    if (searching || initialSelectedDeck !== undefined) return undefined;
    const controller = new AbortController();
    void (async () => {
      try {
        const library = await deckApi.list(false, controller.signal);
        const remembered = library.rememberedDeckId;
        const deck = remembered === null || remembered === undefined
          ? undefined
          : library.decks.find((candidate) =>
              candidate.id === remembered && candidate.validation.ready && candidate.archivedAt === null);
        if (deck !== undefined) {
          setSelectedDeck({
            deckId: deck.id,
            revision: deck.revision,
            name: deck.name,
            cardIds: deck.cardIds,
            passiveId: deck.passiveId,
          });
        }
      } catch {
        // Leave the plaque empty; the picker is one click away.
      }
    })();
    return () => controller.abort();
  }, [deckApi, initialSelectedDeck, searching]);
  const deckChosen = selectedDeck !== undefined;

  const elapsedSeconds = search === undefined
    ? 0
    : Math.max(0, Math.floor((now - search.startedAt) / 1_000));
  const minutes = Math.floor(elapsedSeconds / 60).toString().padStart(2, '0');
  const seconds = (elapsedSeconds % 60).toString().padStart(2, '0');
  const searchStatus = search === undefined
    ? ''
    : search.reconnecting
      ? 'Связь прервалась — переподключаемся…'
      : search.connecting
        ? 'Подключаемся к очереди…'
        : 'Вы в очереди. Подберём первого свободного игрока.';

  return (
    <Screen
      as="main"
      height="dynamic"
      className="cards-lobby"
      data-testid="lobby"
    >
      <section className="cards-lobby-panel">
        {/* Header, actions and plaque share one reserved height so starting a
            search does not resize the panel — see `.lobby-stack`. */}
        <div className="lobby-stack">
          <PanelHeader
            className="lobby-header"
            kicker={searching ? 'БЫСТРАЯ ИГРА' : <Logo height={30} className="lobby-logo" />}
            kickerClassName="lobby-kicker"
            title={searching ? 'Ищем соперника' : null}
            titleClassName="cards-lobby-title"
          />
          {search === undefined ? (
            <div className="lobby-actions lobby-actions-idle">
              <Button
                type="button"
                className="lobby-primary-action"
                data-testid="find-opponent"
                size="sm"
                data-onboarding="lobby-search"
                disabled={!deckChosen}
                onClick={() => { if (selectedDeck !== undefined) onFindOpponent(selectedDeck); }}
              >
                Найти соперника
              </Button>
              <Button
                type="button"
                className="lobby-primary-action lobby-play-bot"
                data-testid="play-bot"
                variant="secondary"
                size="sm"
                data-onboarding="lobby-bot"
                disabled={!deckChosen}
                onClick={() => { if (selectedDeck !== undefined) onPlayBot(selectedDeck); }}
              >
                Играть с ботом
              </Button>
              <Button
                type="button"
                className="lobby-primary-action lobby-challenges"
                data-testid="open-challenges"
                data-onboarding="lobby-challenges"
                variant="secondary"
                size="sm"
                onClick={onChallenges}
              >
                Челленджи
              </Button>
            </div>
          ) : (
            <div className="lobby-actions lobby-actions-searching" data-testid="matchmaking-search">
              <div className="lobby-search-timer-block">
                <div className="lobby-search-timer" data-testid="search-timer">{minutes}:{seconds}</div>
                <p
                  className="lobby-search-status"
                  data-reconnecting={search.reconnecting ? 'true' : 'false'}
                  role="status"
                  aria-live="polite"
                >
                  {searchStatus}
                </p>
                {search.connectionError !== undefined ? (
                  <small className="lobby-search-error">{search.connectionError}</small>
                ) : null}
              </div>
              <Button
                type="button"
                variant="danger"
                size="sm"
                className="lobby-cancel-action lobby-play-bot"
                data-testid="cancel-search"
                onClick={search.onCancel}
              >
                Отменить поиск
              </Button>
            </div>
          )}

          {searching ? null : (
            <Button
              type="button"
              variant="secondary"
              className="lobby-deck-plaque"
              data-testid="lobby-deck-select"
              aria-label={selectedDeck === undefined ? 'Выбрать колоду' : `Колода «${selectedDeck.name}». Сменить`}
              onClick={() => setDeckModalOpen(true)}
            >
              {selectedDeck === undefined ? (
                <span className="lobby-deck-plaque-empty" data-testid="lobby-deck-empty">Выберите колоду</span>
              ) : (
                <>
                  <DeckGlyphStrip cardIds={selectedDeck.cardIds} />
                  <span className="lobby-deck-plaque-name" data-testid="lobby-deck-name">{selectedDeck.name}</span>
                </>
              )}
              <span className="lobby-deck-plaque-hint">{selectedDeck === undefined ? 'Выбрать' : 'Сменить'}</span>
            </Button>
          )}
        </div>
      </section>

      {deckModalOpen ? (
        <LobbyDeckModal
          api={deckApi}
          csrfToken={csrfToken}
          rememberedDeckId={selectedDeck?.deckId ?? null}
          onClose={() => setDeckModalOpen(false)}
          onChosen={setSelectedDeck}
        />
      ) : null}

      {onboardingTour === undefined ? null : (
        <LobbyOnboardingTour
          progress={onboardingTour.progress}
          csrfToken={onboardingTour.csrfToken}
          api={onboardingTour.api}
          onCompleted={onboardingTour.onCompleted}
        />
      )}
    </Screen>
  );
}
